/**
 * CUANTAS TINTAS LLEVA EL DISENO.
 *
 * En serigrafia cada color es una PANTALLA, y cada pantalla es un coste fijo
 * que no depende de cuantas prendas se hagan. Que el cliente lo vea mientras
 * decide es lo que evita la conversacion incomoda al final: «esto que has
 * hecho, con cinco colores, cuesta el doble».
 *
 * Cuenta lo mismo que cuenta `rules-core` para decidir si se pasa del maximo,
 * y a proposito: si contara distinto, el editor diria cuatro tintas y el
 * servidor rechazaria por cinco.
 */
import { previewRgb, type Color, type Layer } from '@besbello/design-core';

export interface Tinta {
  /** Clave estable del color, la misma que usa la separacion de produccion. */
  clave: string;
  /** Como se llama, para poder decirlo. */
  etiqueta: string;
  rgb: { r: number; g: number; b: number };
  /** Si es una tinta plana de carta (Pantone) o un color libre. */
  esPlana: boolean;
  /** Cuantas capas la usan. */
  capas: number;
}

/**
 * Clave de un color.
 *
 * Un `spot` se identifica por su libro y su codigo —dos «186 C» son la MISMA
 * tinta aunque su vista previa difiera en un punto— y un RGB, por sus tres
 * valores. Mezclarlos contaria como dos tintas lo que en el taller es una.
 */
export function claveDeColor(color: Color): string {
  switch (color.type) {
    case 'spot':
      return `spot:${color.book}:${color.code}`;
    case 'rgb':
      return `rgb:${color.r},${color.g},${color.b}`;
    case 'cmyk':
      return `cmyk:${color.c},${color.m},${color.y},${color.k}`;
    case 'technical_white':
      return 'technical_white';
  }
}

function etiquetaDeColor(color: Color): string {
  switch (color.type) {
    case 'spot':
      return `${color.book} ${color.code}`;
    case 'technical_white':
      return 'blanco técnico';
    default: {
      const { r, g, b } = previewRgb(color);
      return `#${[r, g, b].map((n) => n.toString(16).padStart(2, '0')).join('')}`;
    }
  }
}

/** Todos los colores que usa una capa: relleno, trazo, texto y contorno. */
function coloresDe(capa: Layer): Color[] {
  if (capa.kind === 'text') {
    return [capa.color, ...(capa.outline ? [capa.outline.color] : [])];
  }
  if (capa.kind === 'shape') {
    return [...(capa.fill ? [capa.fill] : []), ...(capa.stroke ? [capa.stroke] : [])];
  }
  /*
    Una imagen es tono continuo: no se cuenta como una tinta ni como mil. En
    serigrafia el aviso que corresponde es GRADIENT_NOT_ALLOWED, que ya existe;
    aqui contarla como una tinta daria una cifra tranquilizadora y falsa.

    La excepcion es una imagen puesta a UNA TINTA: ahi el cliente ya ha decidido
    con que color se imprime, y esa si cuenta.
  */
  if (capa.kind === 'image' && capa.ajustes?.modo === 'una-tinta' && capa.ajustes.tinta) {
    return [capa.ajustes.tinta];
  }
  return [];
}

/** Las tintas del diseno, de mas usada a menos. */
export function contarTintas(capas: readonly Layer[]): Tinta[] {
  const porClave = new Map<string, Tinta>();

  for (const capa of capas) {
    for (const color of coloresDe(capa)) {
      const clave = claveDeColor(color);
      const existente = porClave.get(clave);
      if (existente) {
        existente.capas += 1;
        continue;
      }
      porClave.set(clave, {
        clave,
        etiqueta: etiquetaDeColor(color),
        rgb: previewRgb(color),
        esPlana: color.type === 'spot' || color.type === 'technical_white',
        capas: 1,
      });
    }
  }

  return [...porClave.values()].sort((a, b) => (b.capas !== a.capas ? b.capas - a.capas : a.clave < b.clave ? -1 : 1));
}

/**
 * Si hace falta BASE BLANCA debajo.
 *
 * Un amarillo sobre negro sin base queda marron: el tejido oscuro se ve a
 * traves de la tinta. La regla practica del taller es la luminancia — cuanto
 * mas claro es el color sobre cuanto mas oscura es la prenda, mas falta hace.
 */
export function necesitaBaseBlanca(tintas: readonly Tinta[], prendaOscura: boolean): boolean {
  if (!prendaOscura) return false;
  return tintas.some((t) => {
    if (t.clave === 'technical_white') return false; // el blanco tecnico ES la base
    const luz = (0.2126 * t.rgb.r + 0.7152 * t.rgb.g + 0.0722 * t.rgb.b) / 255;
    return luz > 0.25;
  });
}

/**
 * Cuantas pantallas cuesta esto en serigrafia.
 *
 * La base blanca es una pantalla MAS, y es la que mas sorprende al cliente
 * porque no la ve en el diseno: se imprime debajo.
 */
export function pantallasDeSerigrafia(tintas: readonly Tinta[], conBaseBlanca: boolean): number {
  return tintas.length + (conBaseBlanca ? 1 : 0);
}

/**
 * CARTA PANTONE reducida.
 *
 * Un cliente que pide «el azul de mi logotipo» necesita nombrarlo, no
 * aproximarlo con una rueda de color: en el taller se pide por referencia. No
 * es la carta entera —son mas de mil— sino las que se usan de verdad en
 * serigrafia textil, que es lo que hay en el almacen.
 *
 * Las vistas previas son aproximadas Y LO DICEN: una pantalla no reproduce una
 * tinta plana, y prometerlo seria el mismo error que dar el mockup por prueba
 * de color.
 */
export const CARTA_PANTONE: ReadonlyArray<{ code: string; nombre: string; rgb: { r: number; g: number; b: number } }> = [
  { code: '186 C', nombre: 'rojo', rgb: { r: 200, g: 16, b: 46 } },
  { code: '199 C', nombre: 'rojo claro', rgb: { r: 214, g: 37, b: 68 } },
  { code: '485 C', nombre: 'rojo cálido', rgb: { r: 218, g: 41, b: 28 } },
  { code: '021 C', nombre: 'naranja', rgb: { r: 254, g: 80, b: 0 } },
  { code: '123 C', nombre: 'amarillo', rgb: { r: 255, g: 199, b: 44 } },
  { code: '109 C', nombre: 'amarillo intenso', rgb: { r: 255, g: 209, b: 0 } },
  { code: '355 C', nombre: 'verde', rgb: { r: 0, g: 158, b: 73 } },
  { code: '348 C', nombre: 'verde oscuro', rgb: { r: 0, g: 132, b: 61 } },
  { code: '3268 C', nombre: 'verde agua', rgb: { r: 0, g: 171, b: 132 } },
  { code: '300 C', nombre: 'azul', rgb: { r: 0, g: 94, b: 184 } },
  { code: '286 C', nombre: 'azul intenso', rgb: { r: 0, g: 51, b: 160 } },
  { code: '289 C', nombre: 'azul marino', rgb: { r: 0, g: 33, b: 71 } },
  { code: '2925 C', nombre: 'azul claro', rgb: { r: 0, g: 156, b: 222 } },
  { code: '2685 C', nombre: 'morado', rgb: { r: 86, g: 0, b: 140 } },
  { code: '241 C', nombre: 'magenta', rgb: { r: 176, g: 0, b: 132 } },
  { code: '212 C', nombre: 'rosa', rgb: { r: 240, g: 78, b: 152 } },
  { code: '469 C', nombre: 'marrón', rgb: { r: 99, g: 58, b: 23 } },
  { code: '877 C', nombre: 'plata', rgb: { r: 138, g: 141, b: 143 } },
  { code: 'Black 6 C', nombre: 'negro', rgb: { r: 16, g: 24, b: 32 } },
  { code: 'Cool Gray 8 C', nombre: 'gris', rgb: { r: 136, g: 139, b: 141 } },
  { code: 'White', nombre: 'blanco', rgb: { r: 255, g: 255, b: 255 } },
];

export const LIBRO_PANTONE = 'Pantone Solid Coated';

/** Un color de la carta, listo para meter en el documento. */
export function colorPantone(code: string): Color | null {
  const entrada = CARTA_PANTONE.find((c) => c.code === code);
  if (!entrada) return null;
  return { type: 'spot', book: LIBRO_PANTONE, code: entrada.code, preview: entrada.rgb };
}
